import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import 'primereact/resources/themes/lara-light-green/theme.css';

import { PrimeReactProvider } from 'primereact/api';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useEffect } from 'react';
import {
    getDefaultFontFamily,
    smoothieTamaguiConfig,
    type TamaguiInternalConfig,
    TamaguiProvider,
    Toast,
    ToastProvider,
    ToastViewport,
} from 'smoothie';

import { captureWarning } from '../../logger/Logger';
import { useCustomFonts } from '../hooks/useCustomFonts';
import type { ContainerComponent } from '../types/Common';

export const DesignSystemProvider: ContainerComponent = ({ children }) => {
    const [, fontLoadingError] = useCustomFonts();

    useEffect(() => {
        if (fontLoadingError !== null) {
            captureWarning(
                'Error encountered while loading custom fonts',
                fontLoadingError,
            );
        }
    }, [fontLoadingError !== null]);

    return (
        <TamaguiProvider
            config={smoothieTamaguiConfig as TamaguiInternalConfig}
            defaultTheme="light"
        >
            <PrimeReactProvider>
                <ToastProvider>
                    {children}
                    <Toast />
                    <ToastViewport
                        portalToRoot={true}
                        flexDirection="column"
                        bottom={120}
                        left={0}
                        right={0}
                        $sm={{ bottom: '90vh' }}
                    />
                    <style type="text/css">
                        {`
                        /* Grove custom styles for PrimeReact components */

                        .p-component {
                            font-family: ${getDefaultFontFamily()};
                        }

                        /* Styled all chips to work well for GuideChip */
                        .p-chip.p-component.p-chip-image {
                            background: #ffe9d2;
                            border: 3px solid #ffe9d2;
                        }
                        .p-chip img {
                            object-fit: cover;
                            border-radius: 42%;
                        }

                        /* Unsure why, but the default value enables button content to overflow container */
                        .p-button {
                            overflow: unset;
                            border-radius: 16px;
                        }
                        .p-button.p-button-sm {
                            border-radius: 12px;
                        }
                        .p-button .p-button-label {
                            font-weight: 600;
                        }
                        .p-button.p-button-sm .p-button-label {
                            font-weight: 450;
                        }

                        .p-tabview ul.p-tabview-nav {
                            background: transparent;
                        }

                        .p-tabview .p-tabview-nav li .p-tabview-nav-link {
                            font-weight: 600;
                            padding: 1rem 1.15rem;
                        }
                    
                        .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
                            background: transparent;
                            font-weight: 700;
                        }

                        /* Even when empty message element is empty, it still had padding and border */
                        .p-datatable-emptymessage td {
                            padding: 0;
                            border-width: 0;
                        }

                        /* Condensing table header size */
                        .p-datatable.p-datatable-sm .p-datatable-thead > tr > th {
                            padding: 0.8rem 0.5rem;
                            font-size: 0.7rem;
                        }

                        /* Make table header icons smaller */
                        .p-datatable.p-datatable-sm .p-datatable-thead > tr > th .p-icon {
                            height: 0.8rem;
                            width: 0.8rem;
                        }

                        /* Right-aligned columns need space on the right side for comfort */
                        .p-datatable.p-datatable-sm:not(.datatable-without-right-aligned-margin) tr > .p-align-right {
                            padding-right: 2rem;
                        }

                        /* DataTable text should be selectable for copying */
                        .p-datatable tr > td {
                            user-select: text !important;
                        }

                        /* Condensing all badges */
                        .p-component.p-badge {
                            font-size: 11px;
                        }

                        /* Without a constraint, tooltips would take up full width of page */
                        .p-tooltip {
                            max-width: 300px;
                            font-size: 0.85rem;
                        }
                    `}
                    </style>
                    <ConfirmDialog />
                </ToastProvider>
            </PrimeReactProvider>
        </TamaguiProvider>
    );
};
